<template>
  <!-- #749 · el bloque se plega con su triangulito, como el resto de los grupos del Centro de
       Cobros. El estado NO vive aquí: lo manda la pantalla por `abierto` y se le avisa de que se ha
       pulsado con `alternar`, porque los bloques de esta pantalla se recuerdan todos en la misma
       entrada del navegador y no puede haber dos dueños escribiendo en ella.

       Sin `abierto`, el bloque nace abierto y se comporta como antes de esto. -->
  <bloque-plegable titulo="Comportamiento por forma de pago"
    nota="· toda la cartera que ves, sin los filtros de abajo"
    :abierto="abierto" :resumen="resumenCerrado" :refrescando="cargando"
    :aria-busy="cargando ? 'true' : 'false'" @alternar="$emit('alternar')">

    <!-- EL PERIODO · solo acota lo facturado y lo cobrado. Se dice aquí, junto al selector, y no
         en la letra pequeña del final: quien lo mueve tiene que saber qué mueve.

         Y va FUERA del `v-if` de las cifras, por lo mismo que este cuadro no se desmonta en la
         pantalla (ver `centro-cobros.vue`): lo que la persona ha elegido, y el control con el que
         lo elige, no se puede ir porque falle o se esté repitiendo la lectura que depende de él.
         Dentro, un fallo del cuadro dejaba el aviso con su «Reintentar» y se llevaba los dos
         selectores: no se podía probar otro periodo, que es lo primero que haría cualquiera. El
         periodo elegido sigue ahí —vive en el componente—, así que lo que se ocultaba era solo la
         forma de verlo y de cambiarlo.

         La excepción es el fallo de PERMISO, y es la regla de pantalla de dirección del 05/10: un
         control que no se puede usar no se queda mudo. Con un 403 no hay periodo que valga —cambiarlo
         volvería a fallar igual—, así que en vez de dejar dos selectores que no llevan a ninguna
         parte se enseña solo el aviso, que dice quién lo desbloquea y dónde. -->
    <div class="periodo" v-if="!bloqueadoPorPermiso">
      <label class="periodo-campo">
        <span class="periodo-rotulo">Desde</span>
        <!-- Cada selector dice QUIÉN ha cambiado: lo que se arrastra es el otro extremo. -->
        <select v-model="desdeMes" @change="aplicarPeriodo('DESDE')">
          <option v-for="opcion in opcionesMes" :key="'d-' + opcion.clave" :value="opcion.clave">
            {{ opcion.etiqueta }}
          </option>
        </select>
      </label>
      <label class="periodo-campo">
        <span class="periodo-rotulo">Hasta</span>
        <select v-model="hastaMes" @change="aplicarPeriodo('HASTA')">
          <option v-for="opcion in opcionesMes" :key="'h-' + opcion.clave" :value="opcion.clave">
            {{ opcion.etiqueta }}
          </option>
        </select>
      </label>
      <button type="button" class="boton-limpiar" @click="volverADoceMeses">
        Últimos 12 meses
      </button>
      <!-- Lo que el servidor ha aplicado solo se escribe cuando hay respuesta: mientras se está
           pidiendo o si ha fallado, un «Periodo aplicado: …» de la lectura anterior diría que las
           cifras de abajo son de ese periodo cuando abajo no hay cifras. Los selectores sí siguen,
           que es lo que se puede usar. -->
      <span class="periodo-aplicado" v-if="dto && !cargando">
        Periodo aplicado: {{ dto.periodo }}. El pendiente de la primera tabla es el de hoy
        ({{ formatFecha(dto.fechaCalculo) }}) y el periodo no lo recorta.
      </span>
      <!-- Y mientras se recalcula, su relevo (#201, prueba de dirección del 20/10/2026): el cuadro
           se queda abierto con las cifras que ya tenía, así que hay que decir DE QUÉ periodo son.
           Sin esto, los dos selectores dirían el periodo nuevo encima de unas cifras que todavía
           son del anterior, que es la contradicción que la instrucción permanente prohíbe.

           Va junto a los selectores, que es donde está la mano que acaba de pulsar: el indicador
           de carga pegado al control que lo provoca, no en la cabecera de la pantalla. -->
      <span class="periodo-aplicado periodo-recalculando" v-if="dto && cargando">
        <f7-preloader size="16"></f7-preloader>
        Recalculando el periodo elegido. Hasta que llegue, las cifras de abajo siguen siendo las de
        {{ dto.periodo }}.
      </span>
    </div>

    <!-- El preloader que ocupa el cuadro entero es solo para la PRIMERA carga, cuando no hay nada
         que conservar. Repitiendo la lectura con cifras ya en pantalla, esto desmontaba el cuadro
         entero y lo dejaba en un spinner: es lo que dirección probó el 20/10/2026 y describió como
         «Últimos 12 meses se abre y se vuelve a cerrar». Un cuadro que se cierra solo esconde lo
         que se estaba mirando y además pierde el sitio de la pantalla al encogerse. -->
    <div v-if="cargando && !dto" class="text-align-center cuadro-cargando">
      <f7-preloader></f7-preloader>
    </div>

    <!-- El aviso y, pegado a él, el control que lo arregla (revisión CODEX, ronda 2). Pegado y no
         remitiendo al botón de la cabecera: quien lee el fallo aquí tiene aquí mismo con qué volver
         a intentarlo. El de la cabecera también recarga este cuadro, pero eso no se cuenta en el
         aviso —lo que se ofrece es lo que se tiene delante—.

         Cuando el fallo es de permiso NO se ofrece reintentar: el botón no cambiaría nada y un
         control que no sirve enseña a no leer los avisos. En su lugar dice quién lo desbloquea. -->
    <div class="aviso-error" v-if="error">
      <span>{{ error }}</span>
      <button type="button" class="boton-reintentar" v-if="reintentable" @click="cargar">
        Reintentar
      </button>
    </div>

    <!-- Las cifras esperan a que HAYA cifras, no a que no se esté pidiendo nada (#201, prueba de
         dirección del 20/10/2026). Con `!cargando` aquí, cada cambio de periodo —y el botón
         «Últimos 12 meses» es uno— desmontaba las dos tablas, las notas y el ranking, y los volvía
         a montar un segundo y medio después: desde la silla de quien mira, el cuadro se cerraba
         solo y la pantalla pegaba un salto.

         Lo que no se enseña es nada que afirme algo falso: las cifras que se quedan son las que el
         servidor contestó —y el rótulo de arriba dice de qué periodo son mientras se recalcula—, y
         si la lectura FALLA el carril llama a `limpiar`, que pone `dto` a null y se las lleva. Así
         que no hay ningún estado en el que estas tablas sobrevivan a su propio dato.

         Esta es la misma forma del fallo que el panel «Filtros» de `centro-cobros.vue`, que colgaba
         de `!cargando` y se corrigió en la #749: ahí lo pidió dirección aparte. -->
    <template v-if="dto">

      <!-- LAS DOS VISTAS · dirección las pidió las dos y que se pueda pasar de una a otra. Viajan
           en la misma respuesta, así que cambiar de vista no vuelve a pedir nada. -->
      <div class="recorridos">
        <button type="button" class="recorrido-btn" :class="{'recorrido-btn-activo': vista === 'MEDIO'}"
          :aria-pressed="vista === 'MEDIO' ? 'true' : 'false'"
          @click="vista = 'MEDIO'">Por medio de pago</button>
        <button type="button" class="recorrido-btn" :class="{'recorrido-btn-activo': vista === 'PLAZO'}"
          :aria-pressed="vista === 'PLAZO' ? 'true' : 'false'"
          @click="vista = 'PLAZO'">Por medio y plazo</button>
      </div>

      <!-- ===================================================== LO PENDIENTE (HOY) -->
      <p class="bloque-subtitulo">Lo que sigue sin cobrarse, hoy</p>

      <!-- LA TRAMPA, pegada a la tabla que la tiene. Va ARRIBA de esta tabla y no al final de la
           pantalla porque es la advertencia de cómo leer SUS números: quien mire «520 días» tiene
           que encontrar aquí que eso no es el tiempo medio de cobro. -->
      <div class="nota-dato nota-dato-alerta">
        <f7-icon f7="exclamationmark_triangle" class="nota-dato-icono-alerta"></f7-icon>
        <span>{{ dto.notaRetrasoPendiente }}</span>
      </div>

      <div class="tabla-scroll">
        <table class="tabla">
          <thead>
            <tr>
              <th scope="col">Forma de pago</th>
              <th scope="col" class="num">Docs</th>
              <th scope="col" class="num">Pendiente</th>
              <th scope="col" class="num">% vencido (€)</th>
              <th scope="col" class="num">% vencido (docs)</th>
              <th scope="col" class="num">Retraso medio de lo pendiente</th>
              <th scope="col" class="num">Sin plazo pactado</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="fila in filas" :key="'p-' + fila.clave"
              :class="{'fila-sin-dato': fila.sinPlazoPactado}">
              <th scope="row">
                {{ fila.etiqueta }}
                <span class="marca-fuera" v-if="fila.sinPlazoPactado">no se puede medir el plazo</span>
              </th>
              <td class="num">{{ fila.documentos }}</td>
              <td class="num">{{ formatEurosConUnidad(fila.pendiente) }}</td>
              <!-- El importe vencido va SIEMPRE debajo del porcentaje, y es lo que hace legible la
                   raya: el porcentaje no se publica cuando no cae entre 0 y 100 %, que es lo que
                   pasa en una forma de pago cuyos abonos abiertos dejan el vencido fuera de esa
                   horquilla (revisión CODEX, ronda 3). Sin el importe al lado, esa raya no diría
                   nada; con él se lee que hay euros vencidos y que lo que no se puede dar es la
                   proporción. -->
              <td class="num" :class="celdaFalta(fila.porcentajeVencidoImporte)">
                {{ formatPorcentaje(fila.porcentajeVencidoImporte) }}
                <span class="celda-nota">
                  {{ formatEurosConUnidad(fila.pendienteVencido) }} vencidos
                </span>
                <span class="celda-nota" v-if="falta(fila.porcentajeVencidoImporte)">
                  no se puede dar en %: el vencido no es una parte del pendiente de esta fila
                </span>
              </td>
              <td class="num" :class="celdaFalta(fila.porcentajeVencidoDocumentos)">
                {{ formatPorcentaje(fila.porcentajeVencidoDocumentos) }}
              </td>
              <!-- La media con el recuento que la sostiene pegado debajo: una media de una sola
                   factura no se lee igual que la misma media de doscientas. -->
              <td class="num" :class="celdaFalta(fila.retrasoPendientePonderadoDias)">
                {{ formatDias(fila.retrasoPendientePonderadoDias) }}
                <span class="celda-nota" v-if="fila.documentosRetrasoPendiente">
                  sobre {{ fila.documentosRetrasoPendiente }} docs · sin ponderar
                  {{ formatDias(fila.retrasoPendienteSinPonderarDias) }}
                </span>
                <span class="celda-nota" v-else>nada medible</span>
              </td>
              <!-- Cuántos de SUS documentos se quedan fuera de esa media, en esta misma fila: sin
                   esto, la media del medio parecería hecha con todos sus documentos. -->
              <td class="num" :class="celdaFalta(fila.documentosSinPlazoPactado || null)">
                {{ fila.documentosSinPlazoPactado ? fila.documentosSinPlazoPactado : '—' }}
                <span class="celda-nota" v-if="fila.documentosSinPlazoPactado">
                  {{ formatEurosConUnidad(fila.importeSinPlazoPactado) }}
                </span>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <!-- El total lo manda el servidor y la pantalla NO lo suma: es la cifra con la que se
                 coteja el criterio 1 contra el listado del día, y una pantalla que suma lo que
                 quiere no comprueba nada. Mismo criterio que el pie de la escalera. -->
            <tr>
              <th scope="row">Total de la cartera que ves</th>
              <td class="num">{{ dto.documentosPendientes }}</td>
              <td class="num">{{ formatEurosConUnidad(dto.pendienteTotal) }}</td>
              <td class="num"></td>
              <td class="num"></td>
              <td class="num"></td>
              <td class="num"></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p class="nota-dato nota-dato-pie">{{ dto.notaPendiente }}</p>

      <!-- ===================================================== LO COBRADO (DEL PERIODO) -->
      <p class="bloque-subtitulo bloque-subtitulo-separado">
        Lo que se cobró de verdad · {{ dto.periodo }}
      </p>

      <!-- D-COB-7 · de qué está calculando: sobre cuántas facturas y de cuántas no hay fecha de
           cobro. Va JUNTO a la tabla a la que afecta, no al principio de la pantalla. -->
      <div class="nota-dato" :class="{'nota-dato-alerta': !hayCobrosMedidos}">
        <f7-icon :f7="hayCobrosMedidos ? 'info_circle' : 'exclamationmark_triangle'"
          :class="hayCobrosMedidos ? 'nota-dato-icono' : 'nota-dato-icono-alerta'"></f7-icon>
        <span>
          De las {{ dto.facturasDelPeriodo }} facturas del periodo consta fecha de cobro en
          {{ dto.facturasConFechaDeCobro }} y falta en {{ dto.facturasSinFechaDeCobro }};
          se han podido medir contra su plazo pactado {{ dto.facturasMedidas }}.
          <!-- Los abonos y los documentos sin tipo no se cuentan arriba —no se cobran, así que no
               les «falta» la fecha de cobro— pero tampoco se callan (D-COB-8): restan de lo
               facturado y de ahí sale que una fila pueda tener el «Facturado» en negativo. Solo si
               hay alguno. -->
          <template v-if="dto.abonosDelPeriodo">
            Hay además {{ dto.abonosDelPeriodo }} abonos, que restan de lo facturado y no entran en
            ninguna cifra del cobro.
          </template>
          <template v-if="dto.documentosSinTipoDelPeriodo">
            Y {{ dto.documentosSinTipoDelPeriodo }} documentos que no dicen si son factura o abono:
            no aportan euros a lo facturado, porque sin el tipo no se sabe su signo.
          </template>
          <!-- Y el motivo no es siempre el mismo: puede faltar la fecha de cobro (D-COB-7) o
               puede que las que la tienen no se hayan terminado de pagar (ronda 3). Se nombran
               los dos para que esto no contradiga al aviso de debajo, que dice cuántas son. -->
          <template v-if="!hayCobrosMedidos">
            Mientras no haya ninguna medible, esta tabla sale a rayas: no es un fallo de la
            pantalla, es que todavía no hay nada que medir —falta la fecha de cobro, o las que la
            tienen no se han terminado de pagar—. {{ dto.notaRetrasoReal }}
          </template>
        </span>
      </div>

      <!-- #204 · las fechas aproximadas arrastran el ancho de su ventana. Solo si hay alguna. -->
      <div class="nota-dato nota-dato-alerta" v-if="dto.notaFechaAproximada">
        <f7-icon f7="exclamationmark_triangle" class="nota-dato-icono-alerta"></f7-icon>
        <span>{{ dto.notaFechaAproximada }}</span>
      </div>

      <!-- Las facturas con un pago a cuenta que todavía no se han terminado de cobrar: su dinero
           está en «Cobrado» y no dicen nada de la puntualidad (revisión CODEX, ronda 3). Va aquí,
           junto a la tabla del cobro, porque es de lo que esta tabla NO está midiendo. -->
      <div class="nota-dato nota-dato-alerta" v-if="dto.notaSinLiquidar">
        <f7-icon f7="exclamationmark_triangle" class="nota-dato-icono-alerta"></f7-icon>
        <span>{{ dto.notaSinLiquidar }}</span>
      </div>

      <!-- Y las dos formas de medir con el dato a medias: sin todos los cobros (el retraso puede
           quedarse corto) y sin su importe (no pesan en el ponderado). D-COB-8: se mide y se dice
           el error que arrastra, que es el mismo trato que la fecha aproximada. -->
      <div class="nota-dato nota-dato-alerta" v-if="dto.notaCobrosIncompletos">
        <f7-icon f7="exclamationmark_triangle" class="nota-dato-icono-alerta"></f7-icon>
        <span>{{ dto.notaCobrosIncompletos }}</span>
      </div>
      <div class="nota-dato nota-dato-alerta" v-if="dto.notaSinImporte">
        <f7-icon f7="exclamationmark_triangle" class="nota-dato-icono-alerta"></f7-icon>
        <span>{{ dto.notaSinImporte }}</span>
      </div>

      <!-- D-COB-8 · apuntes de cobro que la lectura no ha podido usar: su origen no se reconoce o
           no están guardados como un apunte. Cambia lo que se mide, así que se dice. Solo si hay
           alguno, que es lo raro. -->
      <div class="nota-dato nota-dato-alerta" v-if="dto.notaApuntesNoLeidos">
        <f7-icon f7="exclamationmark_triangle" class="nota-dato-icono-alerta"></f7-icon>
        <span>{{ dto.notaApuntesNoLeidos }}</span>
      </div>

      <div class="tabla-scroll">
        <table class="tabla">
          <thead>
            <tr>
              <th scope="col">Forma de pago</th>
              <th scope="col" class="num">Facturado</th>
              <th scope="col" class="num">Cobrado</th>
              <th scope="col" class="num">% en plazo (€)</th>
              <th scope="col" class="num">% en plazo (facturas)</th>
              <th scope="col" class="num">Retraso real</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="fila in filas">
              <tr :key="'c-' + fila.clave" :class="{'fila-sin-dato': fila.sinPlazoPactado}">
                <th scope="row">{{ fila.etiqueta }}</th>
                <td class="num">
                  {{ formatEurosConUnidad(fila.facturado) }}
                  <!-- «documentos» y no «facturas»: aquí dentro van también los abonos emitidos,
                       que son los que pueden dejar este importe en negativo. -->
                  <span class="celda-nota" v-if="fila.documentosFacturados">
                    {{ fila.documentosFacturados }} documentos emitidos
                  </span>
                </td>
                <td class="num">
                  {{ formatEurosConUnidad(fila.cobrado) }}
                  <span class="celda-nota" v-if="fila.facturasConCobro">
                    {{ fila.facturasConCobro }} facturas con cobro
                  </span>
                </td>
                <td class="num" :class="celdaFalta(fila.porcentajeEnPlazoImporte)">
                  {{ formatPorcentaje(fila.porcentajeEnPlazoImporte) }}
                </td>
                <td class="num" :class="celdaFalta(fila.porcentajeEnPlazoFacturas)">
                  {{ formatPorcentaje(fila.porcentajeEnPlazoFacturas) }}
                  <span class="celda-nota" v-if="fila.facturasMedidas">
                    {{ fila.facturasEnPlazo }} de {{ fila.facturasMedidas }}
                  </span>
                </td>
                <td class="num" :class="celdaFalta(fila.retrasoRealPonderadoDias)">
                  {{ formatDias(fila.retrasoRealPonderadoDias) }}
                  <span class="celda-nota" v-if="fila.facturasMedidas">
                    sobre {{ fila.facturasMedidas }} facturas · sin ponderar
                    {{ formatDias(fila.retrasoRealSinPonderarDias) }}
                  </span>
                  <!-- Los dos motivos por los que una factura cobrada no se ha podido medir van
                       separados: el plazo se arregla en la condición de pago de Business Central y
                       el vencimiento en la propia factura. -->
                  <span class="celda-nota" v-if="fila.facturasCobradasSinPlazo">
                    {{ fila.facturasCobradasSinPlazo }} sin plazo pactado
                  </span>
                  <span class="celda-nota" v-if="fila.facturasCobradasSinVencimiento">
                    {{ fila.facturasCobradasSinVencimiento }} sin fecha de vencimiento
                  </span>
                  <!-- El tercer motivo, y el único que no hay que arreglar: cobraron algo dentro
                       del periodo pero terminan de pagarse después, así que todavía no se sabe si
                       pagaron en plazo. Se mide solo en cuanto el periodo alcance su último cobro. -->
                  <span class="celda-nota" v-if="fila.facturasConCobroPosterior">
                    {{ fila.facturasConCobroPosterior }} siguen pagándose después del periodo
                  </span>
                  <!-- Y el cuarto, que es distinto del tercero y no se arregla con el tiempo: no
                       se han terminado de pagar. Contándolas en plazo por el pago a cuenta que cae
                       dentro del periodo, el cuadro decía que un cliente paga puntual teniendo
                       cientos de euros suyos sin cobrar (revisión CODEX, ronda 3). -->
                  <span class="celda-nota" v-if="fila.facturasSinLiquidar">
                    {{ fila.facturasSinLiquidar }} sin terminar de cobrarse
                  </span>
                  <!-- Las que sí se miden pero con el dato a medias: se dice el error que arrastra
                       la cifra en vez de publicarla como si fuera exacta. -->
                  <span class="celda-nota" v-if="fila.facturasMedidasConCobrosIncompletos">
                    {{ fila.facturasMedidasConCobrosIncompletos }} medidas con cobros que no llegan
                    a su total: su retraso puede quedarse corto
                  </span>
                  <span class="celda-nota" v-if="fila.facturasMedidasSinImporte">
                    {{ fila.facturasMedidasSinImporte }} sin importe de cobro: no pesan en el
                    ponderado
                  </span>
                </td>
                <td>
                  <button type="button" class="boton-gestiones" v-if="(fila.meses || []).length"
                    @click="alternarEvolucion(fila.clave)">
                    {{ abierta === fila.clave ? 'Ocultar meses' : 'Mes a mes' }}
                  </button>
                </td>
              </tr>
              <!-- LA EVOLUCIÓN MES A MES de esa forma de pago, debajo de su propia fila. Solo salen
                   los meses con algún cobro: un mes vacío no es un cero —no es que pagaran tarde,
                   es que no pagaron— y rellenar la serie dibujaría una caída que no ha ocurrido. -->
              <tr v-if="abierta === fila.clave" :key="'m-' + fila.clave" class="fila-evolucion">
                <td :colspan="7">
                  <p class="evolucion-titulo">Mes a mes · {{ fila.etiqueta }}</p>
                  <div class="evolucion">
                    <div v-for="mes in fila.meses" :key="mes.clave" class="evolucion-mes">
                      <span class="evolucion-etiqueta">{{ mes.etiqueta }}</span>
                      <span class="evolucion-cifra">
                        {{ formatPorcentaje(mes.porcentajeEnPlazoImporte) }} en plazo (€)
                      </span>
                      <span class="evolucion-cifra">
                        {{ formatDias(mes.retrasoRealPonderadoDias) }} de retraso
                      </span>
                      <span class="evolucion-detalle">
                        {{ formatEurosConUnidad(mes.cobrado) }} ·
                        {{ mes.facturasMedidas }} facturas medidas
                      </span>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <p class="nota-dato nota-dato-pie">{{ dto.notaFacturado }}</p>
      <p class="nota-dato nota-dato-pie">{{ dto.notaEnPlazo }}</p>
      <p class="nota-dato nota-dato-pie">{{ dto.notaRetrasoReal }}</p>

      <!-- ===================================================== LAS SIN PLAZO PACTADO -->
      <!-- D-COB-8 · su propio grupo, con su número de documentos y su importe. Solo si hay alguna. -->
      <div class="sin-plazo" v-if="dto.sinPlazoPactado">
        <p class="bloque-subtitulo bloque-subtitulo-separado">Las que no se pueden medir</p>
        <p class="sin-plazo-cifra">
          {{ dto.sinPlazoPactado.documentos }} documentos abiertos ·
          {{ formatEurosConUnidad(dto.sinPlazoPactado.pendiente) }}
        </p>
        <p class="nota-dato nota-dato-pie">{{ dto.notaSinPlazo }}</p>
      </div>

      <!-- ===================================================== RANKING DE CLIENTES -->
      <p class="bloque-subtitulo bloque-subtitulo-separado">Clientes por puntualidad</p>
      <!-- Las dos tablas vacías no son el mismo caso y no pueden decir lo mismo: una es «no hay
           nada medido todavía» (D-COB-7) y la otra es «hay medidas, pero ninguna dice de qué
           cliente es», que lo cuenta el aviso de debajo. Con un solo texto, el segundo caso diría
           que no hay facturas medidas teniendo el número de ellas escrito más arriba. -->
      <p class="bloque-vacio" v-if="!clientes.length && !hayCobrosMedidos">
        Todavía no hay ninguna factura del periodo con fecha de cobro y plazo pactado, así que no
        se puede ordenar a ningún cliente por puntualidad. Sale en cuanto lleguen las fechas de
        cobro de Business Central (D-COB-7).
      </p>
      <div class="tabla-scroll" v-if="clientes.length">
        <table class="tabla">
          <thead>
            <tr>
              <th scope="col">Cliente</th>
              <th scope="col" class="num">Facturas medidas</th>
              <th scope="col" class="num">Retraso real</th>
              <th scope="col" class="num">En plazo</th>
              <th scope="col" class="num">Cobrado</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="cliente in clientes" :key="'cli-' + cliente.codCliente">
              <th scope="row">{{ texto(cliente.etiqueta) }}</th>
              <!-- El recuento NO es una nota al pie: es lo que dice si la media se sostiene. Va en
                   su propia columna y pegado a la media, que es lo que pidió el enunciado. -->
              <td class="num" :class="{'num-alerta': cliente.facturasMedidas === 1}">
                {{ cliente.facturasMedidas }}
                <span class="marca-edad" v-if="cliente.facturasMedidas === 1">
                  una sola factura
                </span>
              </td>
              <td class="num">
                {{ formatDias(cliente.retrasoRealPonderadoDias) }}
                <span class="celda-nota">
                  sin ponderar {{ formatDias(cliente.retrasoRealSinPonderarDias) }}
                </span>
              </td>
              <td class="num">
                {{ formatPorcentaje(cliente.porcentajeEnPlazoFacturas) }}
                <span class="celda-nota">
                  {{ cliente.facturasEnPlazo }} de {{ cliente.facturasMedidas }}
                </span>
              </td>
              <td class="num">{{ formatEurosConUnidad(cliente.cobrado) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="bloque-nota" v-if="dto.clientesOmitidos">
        Se enseñan los {{ clientes.length }} primeros; {{ dto.clientesOmitidos }} clientes más no
        caben en la tabla.
      </p>
      <!-- D-COB-8 · las facturas medidas que no dicen de qué cliente son NO están en esta tabla, y
           eso se dice aquí mismo: una tabla a la que le faltan filas sin avisar se lee como si
           fueran todas. Va junto al ranking y sale también cuando el ranking está vacío, que es
           cuando más engañaría. -->
      <div class="nota-dato nota-dato-alerta" v-if="dto.notaSinCliente">
        <f7-icon f7="exclamationmark_triangle" class="nota-dato-icono-alerta"></f7-icon>
        <span>{{ dto.notaSinCliente }}</span>
      </div>
      <p class="nota-dato nota-dato-pie" v-if="clientes.length">{{ dto.notaPuntualidad }}</p>

      <p class="nota-dato nota-dato-pie">{{ dto.notaRaya }}</p>
    </template>
  </bloque-plegable>
</template>

<style scoped>
/* Los estilos son los de centro-cobros.vue, repetidos aquí porque el `scoped` del padre no llega
   a un componente hijo. Repetidos y no movidos a una hoja común: tocar los estilos de la pantalla
   entera para añadir un cuadro es justo lo que la instrucción permanente de dirección pide no
   hacer —lo nuevo no reorganiza lo que ya funciona—. */
/* La caja y el rótulo del bloque los pinta ahora `BloquePlegable`, que es quien lleva el triángulo
   (#749): con los dos declarados también aquí habría dos cajas, una dentro de la otra. */
.bloque-subtitulo { font-size: 12px; font-weight: 700; color: #4a5a78; margin: 12px 0 6px; }
.bloque-subtitulo-separado { margin-top: 20px; }
.bloque-nota { font-size: 11px; font-weight: 400; color: #7c8db0; margin: 6px 0 0; }
.bloque-vacio { font-size: 12px; color: #7c8db0; margin: 8px 0 0; }
.cuadro-cargando { padding: 12px 0; }

.nota-dato {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 8px 0 0;
  font-size: 12px;
  color: #4a5a78;
  line-height: 1.4;
  background: #F2F7FD;
  border-radius: 8px;
  padding: 8px 10px;
}
.nota-dato-icono { font-size: 16px; color: #185FA5; flex-shrink: 0; }
/* En ámbar y no en rojo: no es un error de la pantalla, es dato que falta. Mismo tono que el resto
   de los avisos del Centro de Cobros. */
.nota-dato-alerta { background: #FDF4E3; color: #6B4A10; }
.nota-dato-icono-alerta { font-size: 16px; color: #9A6411; flex-shrink: 0; }
.nota-dato-pie { margin: 8px 0 0; }

.aviso-error {
  background: #FDECEA;
  border-radius: 8px;
  color: #8E2B20;
  font-size: 13px;
  padding: 8px 10px;
  margin: 8px 0 0;
  /* En línea con el texto del aviso, y en flex a propósito: el botón de reintentar tiene que
     quedar junto a la frase que lo anuncia. */
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.boton-reintentar {
  font-size: 12px;
  padding: 4px 12px;
  border: 1px solid #D9A79F;
  border-radius: 6px;
  background: white;
  color: #8E2B20;
  /* Framework7 le da `width: 100%` a TODO <button> (framework7.bundle.css), así que sin esto el
     botón se estira a lo ancho del aviso y cae en su propia línea, lejos de la frase. Las tres
     líneas no son decoración. */
  width: auto;
  min-width: 0;
  text-align: center;
  flex: 0 0 auto;
}
/* El recuadro de foco, explícito: sobre el fondo teñido del aviso el de serie se pierde. El par de
   `:focus-visible` solo lo retira al pulsar con ratón donde el navegador sabe distinguirlo; un
   navegador que no lo entienda descarta el par y se queda con el recuadro, que es el lado seguro. */
.boton-reintentar:focus { outline: 2px solid #8E2B20; outline-offset: 2px; }
.boton-reintentar:focus:not(:focus-visible) { outline: none; }
.boton-reintentar:focus-visible { outline: 2px solid #8E2B20; outline-offset: 2px; }

/* EL PERIODO */
.periodo { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 10px; margin: 0 0 10px; }
.periodo-campo { display: flex; flex-direction: column; gap: 3px; }
.periodo-rotulo { font-size: 11px; color: #7c8db0; }
.periodo-campo select {
  font-size: 12px;
  padding: 5px 6px;
  border: 1px solid #d7dde8;
  border-radius: 6px;
  background: white;
  color: #1a2744;
}
.periodo-aplicado { font-size: 11px; color: #7c8db0; line-height: 1.35; flex: 1 1 220px; }
/* El relevo de «Periodo aplicado» mientras se recalcula: ocupa su mismo sitio para que la fila de
   los selectores no cambie de alto al pasar de uno a otro. En azul, que es lo que lo distingue de
   la nota en gris de al lado: una dice lo que hay, la otra que está cambiando. */
.periodo-recalculando { display: flex; align-items: center; gap: 6px; color: #185FA5; }
.boton-limpiar {
  font-size: 11px;
  padding: 5px 10px;
  border: 1px solid #d7dde8;
  border-radius: 6px;
  background: white;
  color: #185FA5;
}

/* LAS DOS VISTAS */
.recorridos { display: flex; flex-wrap: wrap; gap: 8px; margin: 0 0 4px; }
.recorrido-btn {
  font-size: 12px;
  padding: 5px 12px;
  border: 1px solid #d7dde8;
  border-radius: 999px;
  background: white;
  color: #4a5a78;
}
.recorrido-btn-activo { background: #185FA5; border-color: #185FA5; color: white; }

/* TABLAS · mismas reglas que las de la pantalla */
.tabla-scroll { overflow-x: auto; margin-top: 8px; }
.tabla { width: 100%; border-collapse: collapse; font-size: 12px; }
.tabla th, .tabla td { padding: 6px 8px; border-bottom: 1px solid #eef1f6; white-space: nowrap; }
.tabla thead th {
  text-align: left;
  color: #7c8db0;
  font-size: 11px;
  text-transform: uppercase;
  border-bottom: 1px solid #e0e6f0;
}
.tabla tbody th, .tabla tfoot th { text-align: left; color: #1a2744; font-weight: 600; }
.tabla td { color: #1a2744; }
.tabla .num { text-align: right; }
.tabla tfoot th, .tabla tfoot td { border-top: 1px solid #d7dde8; font-weight: 700; }
.num-alerta { color: #B23B2C; font-weight: 600; }
.celda-falta { color: #9aa7bd; font-style: italic; }
.celda-nota { display: block; font-size: 10px; color: #7c8db0; font-style: normal; font-weight: 400; }
.fila-sin-dato td, .fila-sin-dato th { background: #FBF7F1; }
.marca-fuera, .marca-edad {
  font-size: 9px;
  border-radius: 999px;
  padding: 1px 6px;
  margin-left: 4px;
  background: #F3E6D4;
  color: #8A5B12;
  white-space: nowrap;
}
.boton-gestiones {
  font-size: 11px;
  padding: 3px 8px;
  border: 1px solid #d7dde8;
  border-radius: 6px;
  background: white;
  color: #185FA5;
}

/* LA EVOLUCIÓN */
.fila-evolucion td { background: #F7F9FD; white-space: normal; }
.evolucion-titulo { margin: 0 0 6px; font-size: 11px; font-weight: 700; color: #4a5a78; }
.evolucion { display: flex; flex-wrap: wrap; gap: 10px; }
.evolucion-mes {
  border: 1px solid #e0e6f0;
  border-radius: 8px;
  background: white;
  padding: 6px 8px;
  min-width: 150px;
}
.evolucion-etiqueta { display: block; font-size: 11px; font-weight: 700; color: #1a2744; }
.evolucion-cifra { display: block; font-size: 11px; color: #4a5a78; }
.evolucion-detalle { display: block; font-size: 10px; color: #7c8db0; }

.sin-plazo-cifra { margin: 4px 0 0; font-size: 13px; font-weight: 700; color: #8A5B12; }
</style>

<script>
import FactCobro from '../js/db/FactCobro.js';
import BloquePlegable from './BloquePlegable.vue';
import carrilPeticiones from '../js/utils/carrilPeticiones.js';

const NOMBRES_MES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto',
  'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

/**
 * Cuántos meses hacia atrás ofrece el selector de periodo. Es el tope que admite la API
 * (`FactCobroComportamientoFiltroDTO.TOPE_MESES`): pedir más allá no acorta nada, lo recorta el
 * servidor y la respuesta lo diría en su rótulo sin que la pantalla lo hubiera avisado.
 */
const MESES_OFRECIDOS = 60;

/** Los últimos doce meses, que es lo que mira la API cuando no se le dice nada. */
const MESES_POR_OMISION = 12;

/**
 * El cuadro **«Comportamiento por forma de pago»** del Centro de Cobros (COB-8a, #202): cómo paga
 * de verdad cada condición de pago, para decidir la del próximo pedido con el historial delante.
 *
 * **De solo lectura.** Aquí no se cambia el plazo pactado de ninguna factura, no se decide ninguna
 * condición de pago y no se escribe nada: informa para que lo decida una persona.
 *
 * ## Por qué es un componente y no un bloque más de `centro-cobros.vue`
 *
 * Tres motivos, y el tercero es el que decide:
 *
 *  - tiene **su propia lectura y su propio periodo**, así que su carga y la de la cartera son
 *    independientes: mover el periodo de aquí no vuelve a pedir la cartera, y poner un filtro
 *    allí no vuelve a pedir esto;
 *  - **no lo recortan los filtros de la cartera**, igual que la escalera (#200) y las personas
 *    (#189): lo que enseña es la cartera visible entera, que es lo que permite cotejar su
 *    pendiente contra el listado de facturas pendientes del día sin acordarse de qué filtros
 *    había puestos;
 *  - y así **la pantalla que ya funciona no se reorganiza** para hacer sitio a un cuadro nuevo,
 *    que es la instrucción permanente de dirección. El diff en `centro-cobros.vue` es la línea
 *    que lo monta.
 *
 * ## Las dos mitades miden conjuntos distintos
 *
 * Y por eso son dos tablas y no una de veinte columnas:
 *
 *  - **lo pendiente** son los documentos abiertos de HOY, y el periodo no lo recorta. Se puede
 *    medir ya. Mide solo lo que sigue sin cobrarse, así que sus retrasos están exagerados en
 *    términos absolutos y lo que vale es comparar los medios entre sí. Esa advertencia la escribe
 *    el servidor y va **encima de su tabla**, no al final de la pantalla;
 *  - **lo cobrado** son las facturas del periodo con sus fechas de cobro. Es la medida buena y hoy
 *    sale casi vacía porque las fechas no llegan completas de Business Central (D-COB-7): la tabla
 *    dice, junto a ella, sobre cuántas facturas calcula y de cuántas falta la fecha.
 *
 * Los textos de las notas los manda la API: son las decisiones de dirección sobre el dato, no
 * frases de pantalla. Con el texto escrito aquí, el día que la regla cambie esta pantalla seguiría
 * explicando la anterior.
 */
export default {
  name: 'CuadroComportamientoPago',
  components: { BloquePlegable },
  props: {
    /**
     * #749 · Si el bloque está desplegado. Lo decide y lo recuerda la pantalla que lo monta, que es
     * quien tiene el resto de los bloques del Centro de Cobros.
     *
     * **Por omisión abierto**: si la pantalla dejara de pasarlo, el cuadro se ve como siempre. Al
     * revés —nacer cerrado— un cuadro con cifras quedaría escondido sin que nadie lo hubiera
     * plegado.
     */
    abierto: { type: Boolean, default: true }
  },
  data: function() {
    return {
      cargando: false,
      error: null,
      /** Si el fallo que se está enseñando se puede volver a intentar. Un 403 no. */
      reintentable: false,
      dto: null,
      /** Cuál de las dos vistas se está enseñando. */
      vista: 'MEDIO',
      /** La fila cuya evolución mes a mes está desplegada, por su clave. */
      abierta: null,
      desdeMes: null,
      hastaMes: null
    };
  },
  computed: {
    /**
     * Los meses que ofrece el selector, del más reciente al más antiguo.
     *
     * Se calculan del reloj del navegador y **no de la respuesta**: las opciones de un `select` con
     * `v-model` tienen que existir en el mismo ciclo en el que se fija el modelo, y las que
     * dependen de una petición llegan después y dejan el desplegable enseñando el primer valor en
     * vez del elegido. Quien decide el periodo de verdad es el servidor, que devuelve en
     * `dto.periodo` el que ha aplicado; eso es lo que se enseña al lado.
     */
    opcionesMes: function() {
      const hoy = new Date();
      const opciones = [];
      for (let atras = 0; atras < MESES_OFRECIDOS; atras++) {
        const fecha = new Date(hoy.getFullYear(), hoy.getMonth() - atras, 1);
        const anio = fecha.getFullYear();
        const mes = fecha.getMonth() + 1;
        opciones.push({
          clave: anio + '-' + (mes < 10 ? '0' + mes : String(mes)),
          etiqueta: NOMBRES_MES[mes - 1] + ' ' + anio
        });
      }
      return opciones;
    },
    /** Las filas de la vista elegida. Las dos vienen en la misma respuesta. */
    filas: function() {
      if (!this.dto) return [];
      return (this.vista === 'PLAZO' ? this.dto.porMedioYPlazo : this.dto.porMedio) || [];
    },
    clientes: function() {
      return (this.dto && this.dto.clientes) || [];
    },
    /**
     * #749 · Lo esencial del bloque para cuando está plegado: el periodo que el servidor ha
     * aplicado y cuántas facturas tiene dentro.
     *
     * Es el periodo **aplicado** y no el de los dos selectores, por lo mismo que la línea de
     * «Periodo aplicado» de la cabecera: lo que se ha elegido y lo que se ha medido pueden no ser
     * lo mismo todavía. Y vacío mientras no haya respuesta: un periodo escrito sobre un cuadro que
     * ha fallado afirmaría que las cifras que no hay son de ese periodo.
     */
    resumenCerrado: function() {
      if (!this.dto) return '';
      return 'Periodo aplicado: ' + this.dto.periodo + ' · ' + this.dto.facturasDelPeriodo
        + ' facturas del periodo';
    },
    /**
     * Si el fallo que se está enseñando es de permiso, o sea que no hay nada que reintentar ni
     * ningún periodo que probar. Es lo que decide si los selectores se enseñan: con un 403 serían
     * controles que no llevan a ninguna parte (regla de pantalla de dirección, 05/10/2026).
     */
    bloqueadoPorPermiso: function() {
      return !!this.error && !this.reintentable;
    },
    /**
     * Si hay alguna factura medida contra su plazo. Gobierna el tono del aviso de cobertura: con
     * cero medibles la tabla de cobro sale entera a rayas y eso **no** se distingue de un fallo de
     * carga si no se dice.
     */
    hayCobrosMedidos: function() {
      return !!(this.dto && this.dto.facturasMedidas);
    }
  },
  /** El carril de peticiones no es estado de pantalla: en `data` sería reactivo para nada. */
  created: function() {
    const self = this;
    // #472 · El «cargando» lo enciende y lo apaga el carril, que es el único que sabe cuándo ha
    // contestado la petición vigente: una respuesta de otro periodo que llegue tarde no lo apaga
    // —y con él el rótulo de «Recalculando»— mientras la del periodo elegido sigue en vuelo.
    //
    // Lo que NO se le pide es `vaciar`: las cifras del periodo anterior se quedan mientras se
    // recalcula por decisión de dirección del 20/10/2026 (#201), y el rótulo de al lado de los
    // selectores dice de qué periodo son.
    this.carril = carrilPeticiones({
      cargando: function(si) { self.cargando = si; }
    });
    // Si la carga que está en vuelo tiene que cerrar el detalle mes a mes al LLEGAR. Tampoco se
    // pinta, así que tampoco es reactivo. Ver `aplicarPeriodo`.
    this.cerrarDetalleAlLlegar = false;
    const opciones = this.opcionesMes;
    this.hastaMes = opciones[0].clave;
    this.desdeMes = opciones[Math.min(MESES_POR_OMISION - 1, opciones.length - 1)].clave;
  },
  mounted: function() {
    this.cargar();
  },
  methods: {
    /**
     * Pide el cuadro.
     *
     * Por el carril de peticiones, igual que la cartera: mover los dos selectores seguidos lanza
     * varias y las respuestas no vuelven en orden. Sin esto, la respuesta vieja llega después y
     * pinta las cifras de otro periodo encima de las buenas.
     *
     * **Es público a propósito**: lo llama también el botón de recargar de la cabecera, por el
     * `ref` de `centro-cobros.vue` (revisión CODEX, ronda 2). Repite la consulta con el periodo que
     * esté elegido; el periodo por omisión solo lo pone `created`, así que recargar no se lleva por
     * delante lo que alguien haya seleccionado.
     */
    cargar: function() {
      const self = this;
      self.error = null;
      self.reintentable = false;
      self.carril.lanzar(FactCobro.comportamientoPago(self.periodoParaLaApi()), {
        exito: function(respuesta) {
          self.dto = respuesta;
          // El detalle mes a mes de un periodo no vale para otro, así que se cierra al cambiar de
          // periodo —pero AQUÍ, cuando las cifras nuevas entran, y no al pulsar: cerrarlo al
          // pulsar es el mismo defecto que cerraba el cuadro entero, lo que se está mirando se va
          // antes de que haya nada con lo que sustituirlo. Mientras se recalcula, lo que hay
          // delante sigue siendo del periodo de antes y su detalle sigue cuadrando con él.
          if (self.cerrarDetalleAlLlegar) {
            self.abierta = null;
            self.cerrarDetalleAlLlegar = false;
          }
        },
        limpiar: function() {
          // Sin cifras no se deja lo de antes en pantalla: estaría afirmando algo sobre un periodo
          // que este cuadro ya no está enseñando.
          self.dto = null;
        },
        fallo: function(e) {
          const sinPermiso = !!(e && e.status === 403);
          self.reintentable = !sinPermiso;
          self.error = sinPermiso
            ? 'No tienes acceso al comportamiento por forma de pago. Lo abre Administración, desde '
              + 'los permisos del Centro de Cobros.'
            : 'No se ha podido cargar el comportamiento por forma de pago.';
        }
      });
    },
    /** El periodo como lo espera la API: dos pares año-mes. */
    periodoParaLaApi: function() {
      const desde = partirMes(this.desdeMes);
      const hasta = partirMes(this.hastaMes);
      return {
        anioDesde: desde ? desde.anio : null,
        mesDesde: desde ? desde.mes : null,
        anioHasta: hasta ? hasta.anio : null,
        mesHasta: hasta ? hasta.mes : null
      };
    },
    /**
     * Aplica el periodo elegido.
     *
     * Si el «desde» se queda por detrás del «hasta» se arrastra el otro en vez de pedir un periodo
     * imposible: la API lo recortaría a su valor por omisión y el desplegable seguiría enseñando lo
     * que se eligió, o sea dos partes de la pantalla diciendo cosas distintas del mismo periodo.
     *
     * Y se arrastra **el extremo que no se acaba de tocar**, que es a lo que viene el `extremo`
     * (revisión CODEX, ronda 1): arrastrando siempre el «hasta», quien partiera de noviembre de
     * 2025–octubre de 2026 y eligiera octubre de 2025 como fin se encontraba consultando noviembre
     * de 2025–noviembre de 2025 —o sea un solo mes, y ni el que había pedido—. Lo que uno elige se
     * respeta; lo que cede es el otro lado.
     */
    aplicarPeriodo: function(extremo) {
      if (this.desdeMes > this.hastaMes) {
        if (extremo === 'HASTA') {
          this.desdeMes = this.hastaMes;
        } else {
          this.hastaMes = this.desdeMes;
        }
      }
      this.cerrarDetalleAlLlegar = true;
      this.cargar();
    },
    volverADoceMeses: function() {
      const opciones = this.opcionesMes;
      this.hastaMes = opciones[0].clave;
      this.desdeMes = opciones[Math.min(MESES_POR_OMISION - 1, opciones.length - 1)].clave;
      this.cerrarDetalleAlLlegar = true;
      this.cargar();
    },
    alternarEvolucion: function(clave) {
      this.abierta = this.abierta === clave ? null : clave;
    },
    /** Si ese dato no viene: la misma definición de hueco para toda la pantalla (D-COB-8). */
    falta: function(valor) {
      return valor === null || valor === undefined || valor === '';
    },
    /** La clase de una celda sin dato, que es la que la pinta en gris y en cursiva. */
    celdaFalta: function(valor) {
      return { 'celda-falta': this.falta(valor) };
    },
    /**
     * Una fecha del servidor (`2026-10-02`) como se escribe aquí: `02/10/2026`. Sin `new Date()`,
     * que interpreta la cadena en UTC y en Valladolid puede devolver el día de antes. Mismo
     * criterio y mismo código que en `centro-cobros.vue`.
     */
    formatFecha: function(valor) {
      if (!valor) return '—';
      const partes = String(valor).substring(0, 10).split('-');
      if (partes.length !== 3) return String(valor);
      return partes[2] + '/' + partes[1] + '/' + partes[0];
    },
    /** Euros enteros, que es como se leen estas cifras. */
    formatEuros: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      return Math.round(Number(valor)).toLocaleString('es-ES');
    },
    /**
     * Un importe con su unidad, o un guion **sin unidad** cuando no hay dato: con la unidad escrita
     * en la plantilla, un nulo se pinta como «— €» y eso se lee como una cifra de cero euros, que
     * es justo la confusión que el guion viene a evitar (D-COB-8).
     */
    formatEurosConUnidad: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      return this.formatEuros(valor) + ' €';
    },
    formatPorcentaje: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      return Number(valor).toFixed(1).replace('.', ',') + ' %';
    },
    /**
     * Unos días, con el signo puesto cuando son negativos: un retraso de −8 días es que pagaron
     * ocho días antes de vencer, y escribirlo sin signo diría lo contrario.
     */
    formatDias: function(valor) {
      if (valor === null || valor === undefined || valor === '') return '—';
      const dias = Math.round(Number(valor));
      return dias.toLocaleString('es-ES') + ' días';
    },
    /** Un texto de columna, con el hueco como guion. */
    texto: function(valor) {
      return valor === null || valor === undefined || valor === '' ? '—' : String(valor);
    }
  }
};

/** `2026-03` como el par que espera la API. */
function partirMes(clave) {
  if (!clave) return null;
  const partes = String(clave).split('-');
  if (partes.length !== 2) return null;
  return { anio: Number(partes[0]), mes: Number(partes[1]) };
}
</script>
